/**
 * Projectors for the remaining body sections: accomplishments, honors,
 * employment, references, military service, essays, languages, additional
 * information, demographics, supporting documents, and certification.
 *
 * WHAT MAKES THESE RISKY
 *
 * Two distinct hazards live here.
 *
 * The CHECKLIST sections (accomplishments, honors, acknowledgements) are 50-odd
 * yes/no items whose labels used to exist only inside the applicant form's
 * templates. They project to selected-only lists with a denominator — see
 * `checklist.ts` for why.
 *
 * MILITARY SERVICE and DEMOGRAPHICS both encode "declined to answer" as real
 * data, and that must survive projection. A veteran who answered "no response /
 * not applicable" is not the same as an applicant who never reached the section,
 * and an applicant who explicitly chose "prefer not to answer" on race is not the
 * same as one who left it blank. Collapsing either into "Not provided" destroys a
 * distinction the questionnaire went out of its way to capture.
 */

import type {
	ICertifyAndSubmit,
	IEmployment,
	IEmploymentEntry,
	IEssays,
	IHonorsAndAwards,
	ILanguageEntry,
	IProfessionalAccomplishments,
	IReference,
	ISupportingDocument,
	MilitaryService,
} from '../../oasis-application.types';
import { VETERAN_PREFERENCE_CATEGORY_LABELS } from '../../oasis-application.types';
import type { IDemographics } from '../../oasis-demographics.types';
import {
	ACCOMPLISHMENT_GROUP_LABELS,
	CERTIFY_AND_SUBMIT_LABELS,
	DEMOGRAPHIC_ETHNICITY_LABELS,
	DEMOGRAPHIC_RACE_LABELS,
	DEMOGRAPHIC_SEX_LABELS,
	DISABILITY_CONDITION_LABELS,
	DOC_TYPE_LABELS,
	HONORS_AND_AWARDS_LABELS,
	LANGUAGE_PROFICIENCY_LABELS,
	LITIGATION_EXPERIENCE_LABELS,
	MILITARY_BRANCH_LABELS,
	MILITARY_SERVICE_STATUS_LABELS,
	ORAL_ADVOCACY_LABELS,
	PAYMENT_TYPE_LABELS,
	PUBLIC_SERVICE_LABELS,
	REFERENCE_RELATIONSHIP_LABELS,
	RESEARCH_AND_WRITING_LABELS,
	SERVICE_CHARACTERIZATION_LABELS,
	WORK_SCHEDULE_LABELS,
} from '../oasis-display-labels';
import { formatPhone } from '../oasis-display-format';
import type { DisplayBlock, DisplayField } from '../oasis-display.types';
import { affirmationBlock, checklistBlock } from './checklist';
import { dateRangeField, definedOnly, enumField, enumListField, field, fileRef, longTextField, textField, yesNoField } from './field-builders';
import { definedBadges } from './identity.projectors';

// ─── Professional accomplishments ─────────────────────────────────────────────

export function projectProfessionalAccomplishments(data: IProfessionalAccomplishments): DisplayBlock[] {
	return [
		checklistBlock(ACCOMPLISHMENT_GROUP_LABELS.researchAndWriting, data?.researchAndWriting, RESEARCH_AND_WRITING_LABELS),
		checklistBlock(ACCOMPLISHMENT_GROUP_LABELS.oralAdvocacy, data?.oralAdvocacy, ORAL_ADVOCACY_LABELS),
		checklistBlock(ACCOMPLISHMENT_GROUP_LABELS.publicService, data?.publicService, PUBLIC_SERVICE_LABELS),
		checklistBlock(ACCOMPLISHMENT_GROUP_LABELS.litigationExperience, data?.litigationExperience, LITIGATION_EXPERIENCE_LABELS),
	];
}

// ─── Honors & awards ──────────────────────────────────────────────────────────

export function projectHonorsAndAwards(data: IHonorsAndAwards): DisplayBlock[] {
	return [checklistBlock(undefined, data, HONORS_AND_AWARDS_LABELS)];
}

// ─── Employment ───────────────────────────────────────────────────────────────

/**
 * `sourceActivity` marks a row DERIVED from a clerkship or fellowship on the
 * Education & Experience timeline. Those rows are read-only on the applicant's
 * Employment screen because the timeline owns them, and the badge carries that
 * provenance through so a reviewer seeing the same job in two sections knows it
 * was entered once rather than twice.
 */
export function projectEmployment(data: IEmployment): DisplayBlock[] {
	return [
		{
			kind: 'entries',
			heading: 'DOJ experience',
			entries: (data?.dojExperience ?? []).map(projectEmploymentEntry),
			emptyText: 'No DOJ experience reported.',
		},
		{
			kind: 'entries',
			heading: 'Other experience',
			entries: (data?.otherExperience ?? []).map(projectEmploymentEntry),
			emptyText: 'No other experience reported.',
		},
	];
}

function projectEmploymentEntry(entry: IEmploymentEntry) {
	const provenance =
		entry.sourceActivity === 'clerkship'
			? 'From timeline clerkship'
			: entry.sourceActivity === 'fellowship'
				? 'From timeline fellowship'
				: undefined;

	return {
		title: entry.jobTitle?.trim() || entry.employer?.trim() || 'Position',
		subtitle: entry.employer?.trim(),
		badges: definedBadges([
			entry.workSchedule ? WORK_SCHEDULE_LABELS[entry.workSchedule] : undefined,
			entry.payment ? PAYMENT_TYPE_LABELS[entry.payment] : undefined,
			provenance,
		]),
		fields: definedOnly([
			dateRangeField('Dates', entry.fromDate, entry.toDate),
			textField('Location', [entry.city, entry.stateOrCountry].filter((p) => !!p?.trim()).join(', ')),
			textField('Supervisor', [entry.supervisorName, entry.supervisorTitle].filter((p) => !!p?.trim()).join(', ')),
			textField('Supervisor phone', formatPhone(entry.supervisorPhone)),
			longTextField('Duties and responsibilities', entry.dutiesResponsibilities),
		]),
	};
}

// ─── References ───────────────────────────────────────────────────────────────

/**
 * `relationToApplicant` is stored as free text even though the form offers a
 * dropdown, so a stored value may be either a `ReferenceRelationship` code or
 * something the applicant typed. It is resolved through the label map when it
 * matches a known code and passed through unchanged otherwise.
 */
export function projectReferences(data: readonly IReference[]): DisplayBlock[] {
	return [
		{
			kind: 'entries',
			entries: (data ?? []).map((reference) => ({
				title: reference.name?.trim() || 'Reference',
				subtitle: relationLabel(reference.relationToApplicant),
				fields: definedOnly([
					textField('Title', reference.title),
					textField('Company', reference.company),
					textField('Location', reference.cityState),
					textField('Phone', formatPhone(reference.phone)),
					textField('Email', reference.email),
				]),
			})),
			emptyText: 'No references provided.',
		},
	];
}

function relationLabel(relation: string | undefined): string | undefined {
	if (!relation?.trim()) return undefined;
	const key = relation.trim() as keyof typeof REFERENCE_RELATIONSHIP_LABELS;
	return REFERENCE_RELATIONSHIP_LABELS[key] ?? relation.trim();
}

// ─── Military service ─────────────────────────────────────────────────────────

/**
 * Preference category, shown only when the applicant actually claims eligibility.
 *
 * Reads `preferenceCategory` ONLY when `isEligibleForPreference === 'yes'`, rather than
 * rendering whatever is stored and merely varying the absence reason. That distinction
 * matters because stored bodies can contradict themselves: until Sept 2026 the Military
 * Service screen wrote the category out of a DISABLED control, so an applicant who chose
 * a category, saved, and then answered "not eligible" — or picked an "Other"
 * characterization, which forces ineligibility — kept the category in their body.
 *
 * `enumField` renders any non-blank value and consults its fallback only when the value
 * is blank, so those rows displayed "Eligible for veterans' preference: No" directly
 * above "Preference category: CPS". A reviewer read a 10-point claim the applicant had
 * withdrawn, and veterans' preference is a hiring factor.
 *
 * The writer is fixed, but this is the half that covers bodies ALREADY stored that way —
 * no migration required. It is also the right rule on its own terms: the eligibility
 * answer decides whether this field applies at all, so the display should not show a
 * value the answer says is not applicable.
 *
 * Shared by both arms of the union deliberately. Duplicating the guard is how one arm
 * would quietly keep the old behaviour.
 */
function preferenceCategoryField(data: Extract<MilitaryService, { isEligibleForPreference: unknown }>): DisplayField {
	const claimed = data.isEligibleForPreference === 'yes';
	return enumField(
		'Preference category',
		claimed ? data.preferenceCategory : undefined,
		VETERAN_PREFERENCE_CATEGORY_LABELS,
		claimed ? 'not_provided' : 'not_applicable',
	);
}

export function projectMilitaryService(data: MilitaryService): DisplayBlock[] {
	const status = enumField('Service status', data?.serviceStatus, MILITARY_SERVICE_STATUS_LABELS);

	if (data?.serviceStatus === 'served') {
		const blocks: DisplayBlock[] = [
			{
				kind: 'fields',
				fields: [
					status,
					enumField('Character of service', data.characterization, SERVICE_CHARACTERIZATION_LABELS),
					yesNoField('Eligible for veterans’ preference', data.isEligibleForPreference),
					preferenceCategoryField(data),
				],
			},
		];

		if ((data.servicePeriods ?? []).length) {
			blocks.push({
				kind: 'entries',
				heading: 'Periods of service',
				entries: data.servicePeriods.map((period) => ({
					title: period.branch ? MILITARY_BRANCH_LABELS[period.branch] : 'Service period',
					fields: definedOnly([dateRangeField('Dates', period.from, period.to)]),
				})),
				emptyText: 'No periods of service recorded.',
			});
		}

		return blocks;
	}

	if (data?.serviceStatus === 'derivative_eligible') {
		return [
			{
				kind: 'fields',
				fields: [status, yesNoField('Eligible for veterans’ preference', data.isEligibleForPreference), preferenceCategoryField(data)],
			},
		];
	}

	// "No response / not applicable" is an explicit answer, not an empty section.
	return [{ kind: 'fields', fields: [status] }];
}

// ─── Essays ───────────────────────────────────────────────────────────────────

export function projectEssays(data: IEssays): DisplayBlock[] {
	const blocks: DisplayBlock[] = [
		{
			kind: 'fields',
			fields: [longTextField('Why the applicant wants to work for DOJ', data?.mainEssay)],
		},
	];

	const revised = data?.revisedOmbEssays;
	if (revised) {
		blocks.push({
			kind: 'fields',
			heading: 'Additional essay questions',
			fields: [
				longTextField('Constitution', revised.constitution),
				longTextField('Efficiency', revised.efficiency),
				longTextField('Priorities', revised.priorities),
				longTextField('Work ethic', revised.workEthic),
				yesNoField('Certifies the essays are the applicant’s own work', revised.aiCertification),
			],
		});
	}

	return blocks;
}

// ─── Languages ────────────────────────────────────────────────────────────────

export function projectLanguages(data: readonly ILanguageEntry[]): DisplayBlock[] {
	return [
		{
			kind: 'entries',
			entries: (data ?? []).map((entry) => ({
				title: entry.language?.trim() || 'Language',
				fields: definedOnly([
					enumField('Speaking', entry.oralProficiency, LANGUAGE_PROFICIENCY_LABELS),
					enumField('Writing', entry.writtenProficiency, LANGUAGE_PROFICIENCY_LABELS),
					enumField('Listening', entry.listenProficiency, LANGUAGE_PROFICIENCY_LABELS),
				]),
			})),
			emptyText: 'No languages reported.',
		},
	];
}

// ─── Additional information ───────────────────────────────────────────────────

export function projectAdditionalInformation(data: string | undefined): DisplayBlock[] {
	return [{ kind: 'fields', fields: [longTextField('Additional information', data)] }];
}

// ─── Demographics ─────────────────────────────────────────────────────────────

/**
 * Voluntary self-identification. Every question here has THREE meaningful
 * outcomes — answered, explicitly declined, and never answered — and the stored
 * shape carries dedicated booleans (`declinedAll`, `racePreferNotToAnswer`,
 * `disabilityNoneApply`, `disabilityPreferNotToAnswer`) precisely so those stay
 * distinguishable. They are surfaced as stated answers rather than collapsed into
 * blanks.
 *
 * A global opt-out short-circuits the rest: when `declinedAll` is set the form
 * clears every other field, so rendering those cleared fields as "Not provided"
 * would read as a dozen skipped questions instead of one deliberate choice.
 */
export function projectDemographics(data: IDemographics): DisplayBlock[] {
	if (data?.declinedAll) {
		return [
			{
				kind: 'fields',
				fields: [field('Response', { kind: 'text', value: 'Declined to answer the demographic questions' })],
			},
		];
	}

	const raceField: DisplayField = data?.racePreferNotToAnswer
		? field('Race', { kind: 'text', value: 'Prefer not to answer' })
		: enumListField('Race', data?.races, DEMOGRAPHIC_RACE_LABELS);

	let disabilityField: DisplayField;
	if (data?.disabilityPreferNotToAnswer) {
		disabilityField = field('Disability or serious health condition', { kind: 'text', value: 'Prefer not to answer' });
	} else if (data?.disabilityNoneApply) {
		disabilityField = field('Disability or serious health condition', { kind: 'text', value: 'None of the listed conditions apply' });
	} else {
		disabilityField = enumListField('Disability or serious health condition', data?.disabilityConditions, DISABILITY_CONDITION_LABELS);
	}

	return [
		{
			kind: 'fields',
			fields: [
				enumField('Sex', data?.sex, DEMOGRAPHIC_SEX_LABELS),
				enumField('Ethnicity', data?.ethnicity, DEMOGRAPHIC_ETHNICITY_LABELS),
				raceField,
				disabilityField,
			],
		},
	];
}

// ─── Supporting documents ─────────────────────────────────────────────────────

export function projectSupportingDocuments(data: readonly ISupportingDocument[]): DisplayBlock[] {
	return [
		{
			kind: 'files',
			files: (data ?? []).map((doc) => fileRef(doc, DOC_TYPE_LABELS[doc.docType] ?? doc.docType)),
			emptyText: 'No documents uploaded.',
		},
	];
}

// ─── Certify & submit ─────────────────────────────────────────────────────────

export function projectCertifyAndSubmit(data: ICertifyAndSubmit): DisplayBlock[] {
	return [affirmationBlock(undefined, data, CERTIFY_AND_SUBMIT_LABELS)];
}
